// Checklist library (doc 12): items per building type, bilingual titles, level and frequency.
import { api } from "../lib/api";
import { FREQUENCIES } from "../lib/catalog";
import { t, tl } from "../lib/i18n";
import { h, invalidate, local, resource } from "../lib/ui";
import { act, Async, Button, Chip, Dialog, Field, formData, isBusy, openDialog, Select, Toggle } from "../shared/components";
import { PageHead } from "./app";

function itemDialog(item: any | null) {
  openDialog((close) => (
    <Dialog title={item ? tl(item.title) : t("admin.checklists.newTitle")} onClose={close}>
      <form class="stack" onSubmit={(e: Event) => {
        const d = formData(e);
        const body: any = { title: { en: d.en, fr: d.fr }, level: d.level, frequency: d.frequency };
        act("check.save", async () => {
          if (item) await api.patch(`/api/admin/checklist/${item.id}`, body);
          else await api.post("/api/admin/checklist", { ...body, code: d.code, building_type: d.building_type });
          close();
          invalidate("admin.checklist");
        }, t("common.saved"));
      }}>
        <Field label={t("admin.checklists.titleEn")} id="c-en"><input class="input" id="c-en" name="en" required value={item?.title.en ?? ""} /></Field>
        <Field label={t("admin.checklists.titleFr")} id="c-fr"><input class="input" id="c-fr" name="fr" required value={item?.title.fr ?? ""} /></Field>
        <div class="cols-2">
          <Field label={t("admin.checklists.level")} id="c-lv"><Select id="c-lv" name="level" value={item?.level ?? "base"} options={["legal", "base", "optional"].map((x) => ({ value: x, label: t(`checkLevel.${x}`) }))} /></Field>
          <Field label={t("admin.checklists.frequency")} id="c-fq"><Select id="c-fq" name="frequency" value={item?.frequency ?? "every_visit"} options={FREQUENCIES.map((x) => ({ value: x, label: t(`frequency.${x}`) }))} /></Field>
        </div>
        {!item ? (
          <div class="cols-2">
            <Field label={t("fields.building_type")} id="c-bt"><Select id="c-bt" name="building_type" value="all" options={["all", "villa", "strata_unit"].map((x) => ({ value: x, label: t(`buildingType.${x}`) }))} /></Field>
            <Field label={t("admin.checklists.code")} id="c-code" hint={t("admin.roles.keyHint")}><input class="input" id="c-code" name="code" required pattern="[a-z][a-z0-9_]{1,40}" /></Field>
          </div>
        ) : null}
        <div class="row end">
          <Button kind="secondary" onClick={close}>{t("common.cancel")}</Button>
          <Button type="submit" busy={isBusy("check.save")}>{t("common.save")}</Button>
        </div>
      </form>
    </Dialog>
  ));
}

export function ChecklistPage() {
  const r = resource("admin.checklist", () => api.get("/api/admin/checklist"));
  const s = local("screen:checklists", () => ({ type: "all" }));
  const toggle = (item: any, active: boolean) => act(`check.${item.id}`, async () => {
    await api.patch(`/api/admin/checklist/${item.id}`, { active });
    item.active = active;
  });
  return (
    <div class="stack loose">
      <PageHead title={t("admin.checklists.title")} intro={t("admin.checklists.intro")}
        actions={<Button icon="plus" onClick={() => itemDialog(null)}>{t("admin.checklists.add")}</Button>} />
      <div class="seg" role="group">
        {["all", "villa", "strata_unit"].map((x) => (
          <button aria-pressed={String(s.type === x)} onClick={() => s.set({ type: x })}>{t(`admin.checklists.tabs.${x}`)}</button>
        ))}
      </div>
      <section class="panel">
        {Async(r, (rows: any[]) => (
          <div class="table-wrap">
            <table class="table">
              <thead><tr><th>{t("admin.checklists.item")}</th><th>{t("admin.checklists.level")}</th><th>{t("admin.checklists.frequency")}</th><th class="right">{t("admin.checklists.inUse")}</th></tr></thead>
              <tbody>
                {rows.filter((x) => x.building_type === s.type).map((x) => (
                  <tr class="link" onClick={(e: MouseEvent) => !(e.target as Element).closest(".switch") && itemDialog(x)}>
                    <td><span class={x.active ? "strong" : "muted"}>{tl(x.title)}</span><span class="sub">{x.code}</span></td>
                    <td><Chip kind={x.level === "legal" ? "accent" : x.level === "base" ? "info" : "neutral"} plain>{t(`checkLevel.${x.level}`)}</Chip></td>
                    <td class="small">{t(`frequency.${x.frequency}`)}</td>
                    <td class="right"><Toggle checked={x.active} onChange={(v) => toggle(x, v)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ), 8)}
      </section>
    </div>
  );
}
